// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { FramedImageItem } from './FramedImageItem'
import type { CropFrameOverlayEdge } from './CropFrameOverlay'
import type { Corner, CanvasItemData } from './types'

interface FrameImageListProps {
    items: CanvasItemData[]
    contentScale: number
    getNaturalSizeForSrc?: (src: string) => { w: number; h: number } | null
    onActivateLayer?: (frameId: string, layerId: string) => void
    // Mode state (independent toggles)
    frameModeById: Record<string, boolean>
    brushModeById: Record<string, boolean>
    onFrameModeToggle: (id: string) => void
    onBrushModeToggle: (id: string) => void
    // Draw overlay controls (only active in brush mode)
    drawBrushSizeCssPx?: number
    onDrawBrushSizeChange?: (v: number) => void
    drawErasing?: boolean
    onToggleEraser?: () => void
    getDrawHandleRef?: (id: string) => React.RefObject<any> | null
    readOnly?: boolean
    savedNodeIds?: Record<string, boolean>
    onSaveToggle?: (nodeId: string) => void
    handlers: {
        onImageSelect: (id: string, e?: React.PointerEvent) => void
        onImageMoveStart: (id: string, e: React.PointerEvent, src: string) => void
        onImageResizeStart: (id: string, src: string, corner: Corner, e: React.PointerEvent) => void
        
        // New Layer handlers - mapped to Image handlers for now or updated?
        // To minimize changes in parent (Canvas.tsx), we might need to adapt
        onLayerMoveStart?: (frameId: string, layerId: string, e: React.PointerEvent) => void
        onLayerResizeStart?: (frameId: string, layerId: string, corner: Corner, e: React.PointerEvent) => void

        onFrameSelect: (id: string) => void
        onFrameMoveStart: (id: string, e: React.PointerEvent) => void
        onFrameResizeStart: (id: string, edge: CropFrameOverlayEdge, e: React.PointerEvent) => void
        onImageLoad: (src: string) => (e: React.SyntheticEvent<HTMLImageElement, Event>) => void
        onVideoLoad?: (src: string) => (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void
        onImageError?: (src: string) => void
        onImageContextMenu?: (id: string, e: React.MouseEvent, src: string) => void
        
        // Layer management
        onReorderLayers?: (frameId: string, fromIndex: number, toIndex: number) => void
        onRemoveLayer?: (frameId: string, layerId: string) => void
    }
}

export function FramedImageList(props: FrameImageListProps): ReactElement {
    const {
        items,
        contentScale,
        getNaturalSizeForSrc,
        onActivateLayer,
        handlers,
        frameModeById,
        brushModeById,
        onFrameModeToggle,
        onBrushModeToggle,
        drawBrushSizeCssPx,
        onDrawBrushSizeChange,
        drawErasing,
        onToggleEraser,
        getDrawHandleRef,
        readOnly = false,
        savedNodeIds,
        onSaveToggle,
    } = props

    return (
        <>
            {items.map((it) => (
                <FramedImageItem
                    key={it.id}
                    id={it.id}
                    layers={it.layers}
                    frameRect={it.frameRect}
                    contentScale={contentScale}
                    // Helper to get natural size for layers
                    getNaturalSize={(src) => getNaturalSizeForSrc ? getNaturalSizeForSrc(src) : null}
                    
                    activeLayerId={it.activeLayerId}
                    onActivateLayer={(layerId) => {
                        onActivateLayer?.(it.id, layerId)
                    }}
                    onLayerSelect={(layerId, e) => {
                        handlers.onImageSelect(it.id, e)
                        onActivateLayer?.(it.id, layerId)
                    }}
                    onLayerMoveStart={(layerId, e) => {
                        const isFrameMode = Boolean(frameModeById[it.id])
                        const layerSrc =
                            it.layers.find((l) => l.id === layerId)?.src ||
                            it.layers.find((l) => l.id === it.id)?.src ||
                            it.layers[0]?.src ||
                            ''

                        // In Object Mode, moving any layer should move the world item (frame/image) with correct src.
                        if (!isFrameMode) {
                            handlers.onImageMoveStart(it.id, e, layerSrc)
                            return
                        }

                        // Frame Mode: move the specific layer within the frame.
                        if (handlers.onLayerMoveStart) {
                            handlers.onLayerMoveStart(it.id, layerId, e)
                            return
                        }

                        // Legacy fallback: move the world item (best-effort).
                        handlers.onImageMoveStart(it.id, e, layerSrc)
                    }}
                    onLayerResizeStart={(layerId, corner, e) => {
                        const isFrameMode = Boolean(frameModeById[it.id])
                        const layerSrc =
                            it.layers.find((l) => l.id === layerId)?.src ||
                            it.layers.find((l) => l.id === it.id)?.src ||
                            it.layers[0]?.src ||
                            ''

                        // In Object Mode, resizing should resize the world item (frame/image) with correct src.
                        if (!isFrameMode) {
                            handlers.onImageResizeStart(it.id, layerSrc, corner, e)
                            return
                        }

                        // Frame Mode: resize the specific layer within the frame.
                        if (handlers.onLayerResizeStart) {
                            handlers.onLayerResizeStart(it.id, layerId, corner, e)
                            return
                        }

                        // Legacy fallback: resize the world item (best-effort).
                        handlers.onImageResizeStart(it.id, layerSrc, corner, e)
                    }}

                    onFrameSelect={() => handlers.onFrameSelect(it.id)}
                    onFrameMoveStart={(e) => handlers.onFrameMoveStart(it.id, e)}
                    onFrameResizeStart={(edge, e) => handlers.onFrameResizeStart(it.id, edge, e)}
                    
                    onImageLoad={handlers.onImageLoad} // Pass factory
                    onVideoLoad={handlers.onVideoLoad}
                    onImageError={handlers.onImageError}
                    
                    onFrameFit={it.onFrameFit}
                    onFrameConfirm={it.onFrameConfirm}
                    frameSizeText={it.frameSizeText}
                    hasFrameChanges={it.hasFrameChanges}
                    hasDrawingChanges={it.hasDrawingChanges}
                    frameDiffersFromImage={it.frameDiffersFromImage}
                    isConfirming={it.isConfirming}
                    isNodeSelected={it.isNodeSelected}
                    isDragTarget={it.isDragTarget}
                    onImageContextMenu={(layerId, e, src) => handlers.onImageContextMenu?.(layerId, e, src)}
                    frameModeEnabled={frameModeById[it.id] || false}
                    brushModeEnabled={brushModeById[it.id] || false}
                    onFrameModeToggle={() => onFrameModeToggle(it.id)}
                    onBrushModeToggle={() => onBrushModeToggle(it.id)}
                    drawBrushSizeCssPx={drawBrushSizeCssPx}
                    onDrawBrushSizeChange={onDrawBrushSizeChange}
                    drawErasing={drawErasing}
                    onToggleEraser={onToggleEraser}
                    drawHandleRef={getDrawHandleRef?.(it.id) || undefined}
                    readOnly={readOnly}
                    isSaved={savedNodeIds?.[it.id] ?? false}
                    onSaveToggle={onSaveToggle ? () => onSaveToggle(it.id) : undefined}
                    onReorderLayers={handlers.onReorderLayers}
                    onRemoveLayer={handlers.onRemoveLayer}
                />
            ))}
        </>
    )
}
